import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateSelectionListSummary } from './useSelectionListSummary';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { invalidateSelectionListGroupedItems } from './useSelectionListGroupedItems';
import { convertLineItemKeysToDtoFromProductSelection } from '../../shared/utils/convertLineItemKey';
import {
    ChangeAmountByLineItemKeysRequest,
    decreaseAmountBulkInListByLineItemKeys
} from './decreaseAmountBulkInListByLineItemKeys';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../store/constants';
import { showNotification } from '../../store/notifications/actions';
import { failSelectionlists } from '../../store/orderlist/actions';
import { setAmountBulkInListByLineItemKeys } from './setAmountBulkInListByLineItemKeys';
import { increaseAmountBulkInListByLineItemKeys } from './increaseAmountBulkInListByLineItemKeys';
import { ChangeAmountByFilterRequest, decreaseAmountBulkInListByFilter } from './decreaseAmountBulkInListByFilter';
import { setAmountBulkInListByFilter } from './setAmountBulkInListByFilter';
import { increaseAmountBulkInListByFilter } from './increaseAmountBulkInListByFilter';

type GetRequestParams = {
    selectionListId: string;
    productSelection: ProductSelection;
    filterRequest: string;
    amount: number;
    command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE;
};

export const changeAmount = (
    { selectionListId, productSelection, filterRequest, amount, command }: GetRequestParams,
    t: TFunction
) => {
    if (isIncludeProductSelection(productSelection)) {
        const body: ChangeAmountByLineItemKeysRequest = {
            lineItemKeys: convertLineItemKeysToDtoFromProductSelection(productSelection)
        };
        switch (command) {
            case ACTION_DECREASE:
                return decreaseAmountBulkInListByLineItemKeys(selectionListId, body);
            case ACTION_INCREASE:
                return increaseAmountBulkInListByLineItemKeys(selectionListId, body);
            case ACTION_SET:
                return setAmountBulkInListByLineItemKeys(selectionListId, { ...body, targetAmount: amount });
        }
    } else if (isExcludeProductSelection(productSelection)) {
        const body: ChangeAmountByFilterRequest = {
            filterRequest: deserializeFilter(filterRequest, {
                includeKey: 'include',
                excludeKey: 'exclude'
            }),
            excludedLineItemKeys: productSelection.excluded
        };
        switch (command) {
            case ACTION_DECREASE:
                return decreaseAmountBulkInListByFilter(selectionListId, body);
            case ACTION_INCREASE:
                return increaseAmountBulkInListByFilter(selectionListId, body);
            case ACTION_SET:
                return setAmountBulkInListByFilter(selectionListId, { ...body, targetAmount: amount });
        }
    } else {
        throw new Error(t('notifications.buildAddProductsCallback.error'));
    }
};

type Props = {
    filter: string;
    listId: string;
    productSelection: ProductSelection;
};

export const useChangeAmountBulkInList = ({ filter, listId, productSelection }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    return {
        changeAmountBulk: async (
            amount: number,
            command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE,
            callback: () => void
        ) => {
            try {
                const response = await changeAmount(
                    {
                        selectionListId: listId,
                        productSelection,
                        filterRequest: filter,
                        amount,
                        command
                    },
                    t
                );

                dispatch(showNotification(t('notifications.copyAmountChangeListBulk.message')));
                invalidateSelectionListAggregations(listId).then();
                invalidateSelectionListSummary(listId).then();
                invalidateSelectionListGroupedItems(listId).then();
                invalidateSelectionListProducts(listId).then();
                callback();

                return response;
            } catch (error) {
                dispatch(failSelectionlists(error));

                throw error;
            }
        }
    };
};
